import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import { styled, useTheme } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';

import { useRun } from 'src/hooks/api/pipeline/useRun';
import { useFileUpload } from 'src/hooks/api/intent/useFileUpload';
import { useCreateIntent } from 'src/hooks/api/intent/useCreateIntent';
import { PipelineName, PipelineType, useCreateRun } from 'src/hooks/api/pipeline/useCreateRun';

import { API_ERROR_MESSAGES, AUDIO_FILE_FORMATS } from 'src/utils/constants';

import { type FileWithId } from 'src/types';
import { STORAGE_BASE } from 'src/global-config';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify/iconify';
import { FileThumbnail } from 'src/components/file-thumbnail';

import ChatTips from '../ChatTips';
import { CHAT } from '../chatConfig';
import ChatUpload from '../ChatUpload';
import FileName from './components/FileName';
import ChatDisclaimer from '../ChatDisclaimer';
import SendButton from './components/SendButton';
import { useCommonInputProps } from './useCommonInputProps';
import { useMessageContext } from '../hooks/uesMessageContext';
import { useSettingsContext } from '../hooks/useSettingsContext';
import { useChatHistoryContext } from '../hooks/useChatHistoryContext';
import { useNotifyOverflowContext } from '../hooks/useNotifyOverflowContext';

const FlexStack = styled(Stack)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

function ChatInputBox() {
  const {
    history,
    updateAssistantResponse,
    setRunId,
    setIntentId,
    setError,
    setDocumentId,
    setUserMessage,
    setAllFiles,
    allFiles,
    removeFileFromContext,
    currentFileFormat,
    setCurrentFileFormat,
    setIsLoading,
  } = useChatHistoryContext();

  const { message, setMessage, isTranscribing, setIsTranscribing, maxFileSize, setMaxFileSize } =
    useMessageContext();
  const { notify } = useNotifyOverflowContext();
  const { trimValue, commonProps, handleKeyDown, handleChange } = useCommonInputProps();
  const { settings: modelSettings, getModelDetails } = useSettingsContext();
  const { response, error, fileId } = useRun(
    history?.runId as string,
    maxFileSize,
    message.files[0]?.id
  );
  const { createIntent, isIntentPending } = useCreateIntent();
  const { createRun } = useCreateRun();
  const { uploadFile, isUploading, isError } = useFileUpload();
  const theme = useTheme();

  const { W_MAX, PX_INPUT } = CHAT;
  const isEmpty = history.messages.length === 0;
  const emptyPadding = `32px ${PX_INPUT}px 22px ${PX_INPUT}px`;
  const [allowedFileTypes, setAllowedFileTypes] = useState<string>('');

  // Disable the input if the user is uploading a file or if the user has an error or if the user has a file and the message is empty
  // Also disable when a response is being fetched
  const disabled = !trimValue(message.value);
  const shouldDisabled =
    disabled || isUploading || (isError && !message.value) || history.isLoading || isTranscribing;
  const MAX_FILES_ALLOWED =
    currentFileFormat && AUDIO_FILE_FORMATS.includes(currentFileFormat) ? 1 : 5;
  // Flag for file loading state
  const isUploadPending = isIntentPending || isUploading || isTranscribing;

  // Check if max files limit reached
  const canUploadMoreFiles = allFiles.filter((each) => !!each.id).length < MAX_FILES_ALLOWED;

  useEffect(() => {
    if (response?.status === 'COMPLETED' || response?.status === 'FAILED' || error) {
      setIsLoading(false);
      setMaxFileSize(0);
      setIsTranscribing(false);
    }

    if (
      response?.status === 'COMPLETED' &&
      !response?.config?.pipelines[0]?.modules?.includes('preprocess_audio', 'speech_to_text')
    ) {
      const responseKey = `${
        response.details?.chat_with_audio_pipeline_response
          ? PipelineName.CHAT_WITH_AUDIO
          : modelSettings.knowledgeBase.length > 0
            ? PipelineName.CHAT_WITH_KNOWLEDGE_BASE
            : PipelineName.CHATBOT
      }_response`;

      updateAssistantResponse({
        answer: response.details?.chat_with_audio_pipeline_response
          ? ((response?.details as Record<string, any>)?.[responseKey]?.results?.llm_test_module ??
            '')
          : ((response?.details as Record<string, any>)?.[responseKey]?.results?.chatbot_module ??
            ''),
        model: getModelDetails(modelSettings.model).name,
        knowledgeBase: modelSettings.knowledgeBase,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.runId, response, error]);

  useEffect(() => {
    if (!error) return undefined;
    if (currentFileFormat && AUDIO_FILE_FORMATS.includes(currentFileFormat)) {
      setMessage((prev) => ({
        ...prev,
        files: prev.files.filter((eachFile) => eachFile.id !== fileId),
        allFiles: allFiles.filter((eachFile) => eachFile.id !== fileId),
      }));
      setAllFiles(allFiles.filter((eachFile: FileWithId) => eachFile.id !== fileId));
      setCurrentFileFormat('');
      toast.error(API_ERROR_MESSAGES.MAX_ATTEMPTS_EXCEEDED);
    } else {
      setError({
        error: error.message,
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    setAllowedFileTypes(
      getModelDetails(modelSettings.model)
        .fileTypeSupport?.map((each: { label: string; value: string }) => each.value)
        .join(',')
    );
  }, [modelSettings.model, getModelDetails]);

  const onSubmit = () => {
    // Prevent sending empty messages and when the response is pending
    if (!trimValue(message.value) || shouldDisabled) return;

    notify();
    setIsLoading(true);

    // Create file URLs for all uploaded files
    const fileUrls = message.files.map((eachFile) => {
      if (eachFile.isS3Upload) {
        return `s3://${eachFile.currentPath}`;
      } else {
        return `${STORAGE_BASE}/${localStorage.getItem('userId')}/${eachFile.id}/${eachFile.file.name}`;
      }
    });

    setUserMessage({
      message: message.value,
      file: history.documentId && message.files.length > 0 ? message.files : undefined,
      url: history.documentId && fileUrls.length > 0 ? fileUrls : undefined,
      model: getModelDetails(modelSettings.model).name,
      knowledgeBase: modelSettings.knowledgeBase,
      allFiles,
    });

    setMessage({ value: '', files: [], allFiles: [] });

    if (!history.intentId) {
      createIntent(
        {
          title: 'Chat',
          file_ids: [],
          intent_metadata: {},
          pipeline_type: PipelineType.NOT_DEFINED,
          tags: ['ai_studio'],
        },
        {
          onSuccess: async (intentId) => {
            setIntentId({ intentId });
            if (currentFileFormat && AUDIO_FILE_FORMATS.includes(currentFileFormat)) {
              onCreateRun(history.intentId ?? '', 'chat_with_transcript');
            } else {
              onCreateRun(intentId);
            }
          },
        }
      );
    } else {
      if (currentFileFormat && AUDIO_FILE_FORMATS.includes(currentFileFormat)) {
        onCreateRun(history.intentId ?? '', 'chat_with_transcript');
      } else {
        onCreateRun(history.intentId);
      }
    }
    setDocumentId({ documentId: [] });
    setCurrentFileFormat('');
  };

  const onCreateRun = (intentId: string, mode?: string, audioFile?: FileWithId) => {
    // Create file content objects for all uploaded files
    const fileContents = {
      type:
        message.files.length > 0 && message.files[0].file.name.endsWith('.pdf')
          ? 'pdf_file'
          : 'other_files',
      ...(message.files.length > 0 && message.files[0].file.name.endsWith('.pdf')
        ? {
            pdf_file: message.files.map((eachFile) => {
              if (eachFile.isS3Upload) {
                return `s3://${eachFile.currentPath}`;
              } else {
                return `${STORAGE_BASE}/${localStorage.getItem('userId')}/${eachFile.id}/${eachFile.file.name}`;
              }
            }),
          }
        : {
            other_files: AUDIO_FILE_FORMATS.includes(
              `.${message.files[0]?.file?.name?.split('.').pop()}`
            )
              ? message.files.map(
                  (eachFile) =>
                    `${STORAGE_BASE}/${eachFile?.isS3Upload ? eachFile.uuid : eachFile.id}/raw_transcription.txt`
                )
              : message.files.map((eachFile) => {
                  if (eachFile.isS3Upload) {
                    return `s3://${eachFile.currentPath}`;
                  } else {
                    return `${STORAGE_BASE}/${localStorage.getItem('userId')}/${eachFile.id}/${eachFile.file.name}`;
                  }
                }),
          }),
    };
    createRun(
      {
        config: {
          messages: [
            ...history.messages.map(({ role, content, url, file }) => ({
              role,
              content:
                file && file.filter((eachFile) => !!eachFile.id).length > 0
                  ? [
                      { type: 'text', text: content },
                      {
                        type: file[0].file.name.endsWith('pdf') ? 'pdf_file' : 'other_files',
                        ...(file[0].file.name.endsWith('pdf')
                          ? {
                              pdf_file: file
                                .filter((eachFile) => !!eachFile.id)
                                .map((eachFile) => {
                                  if (eachFile.isS3Upload) {
                                    return `s3://${eachFile.currentPath}`;
                                  } else {
                                    return `${STORAGE_BASE}/${localStorage.getItem('userId')}/${eachFile.id}/${eachFile.file.name}`;
                                  }
                                }),
                            }
                          : {
                              other_files: AUDIO_FILE_FORMATS.includes(
                                `.${file[0].file.name.split('.').pop()}`
                              )
                                ? file.map(
                                    (eachFile) =>
                                      `${STORAGE_BASE}/${eachFile?.isS3Upload ? eachFile.uuid : eachFile.id}/raw_transcription.txt`
                                  )
                                : file
                                    .filter((eachFile) => !!eachFile.id)
                                    .map((eachFile) => {
                                      if (eachFile.isS3Upload) {
                                        return `s3://${eachFile.currentPath}`;
                                      } else {
                                        return `${STORAGE_BASE}/${localStorage.getItem('userId')}/${eachFile.id}/${eachFile.file.name}`;
                                      }
                                    }),
                            }),
                      },
                    ]
                  : [{ type: 'text', text: content }],
            })),
            {
              role: 'user',
              content:
                history.documentId && history.documentId?.length > 0
                  ? [{ type: 'text', text: message.value }, fileContents]
                  : [{ type: 'text', text: message.value }],
            },
          ],
          model_config: {
            model_name: modelSettings.model,
            max_output_token_length: modelSettings.maxTokens,
            temperature: modelSettings.temperature,
            top_p: modelSettings.topP,
            top_k: modelSettings.topK,
            knowledge_base: modelSettings.knowledgeBase,
            knowledge_base_type: modelSettings.knowledgeBaseType,
          },
          fileName:
            message.files.length > 0
              ? `${STORAGE_BASE}/${localStorage.getItem('userId')}/${history.documentId}/${message.files[0].file.name}`
              : '',
        },
        intent_id: intentId,
        audio_file: audioFile,
        mode,
      },
      {
        onSuccess: (runId) => {
          setRunId({ runId });
        },
      }
    );
  };

  const handleUpload = (files: File[]) => {
    const selectedFile = files[0];
    if (!canUploadMoreFiles || !allowedFileTypes.includes(selectedFile.type)) return;
    if (message.files.length === 0) {
      const extension = selectedFile.name.split('.').pop();
      setCurrentFileFormat(`.${extension}`);
    }
    // Add the new file to the files array
    setMessage((prev) => ({
      ...prev,
      files: [...prev.files, { id: '', file: selectedFile }],
      allFiles: [...(prev.allFiles || []), { id: '', file: selectedFile }],
    }));
    setAllFiles([...allFiles, { file: selectedFile, id: '' }]);

    if (history.intentId) {
      onFileUpload(history.intentId, selectedFile);
    } else {
      createIntent(
        {
          title: 'Chat',
          file_ids: [],
          intent_metadata: {},
          pipeline_type: PipelineType.NOT_DEFINED,
          tags: ['ai_studio'],
        },
        {
          onSuccess: (intentId) => {
            setIntentId({ intentId });
            onFileUpload(intentId, selectedFile);
          },
        }
      );
    }
  };

  const onFileUpload = (intentId: string, selectedFile: File) => {
    if (!selectedFile) return;
    if (maxFileSize < selectedFile.size) setMaxFileSize(selectedFile.size);
    uploadFile(
      {
        intentId,
        file: selectedFile,
      },
      {
        onSuccess: (res) => {
          setDocumentId({
            documentId: [...(history.documentId ?? []), res.fileId],
          });
          setMessage((prev) => ({
            ...prev,
            files: prev.files.map((eachFile) =>
              eachFile.file.name === selectedFile.name
                ? { ...eachFile, id: res.fileId, uploadedAt: Date.now() }
                : eachFile
            ),
            allFiles: prev.allFiles?.map((eachFile) =>
              eachFile.file.name === selectedFile.name
                ? { ...eachFile, id: res.fileId, uploadedAt: Date.now() }
                : eachFile
            ),
          }));
          if (
            selectedFile.name.split('.').pop() &&
            AUDIO_FILE_FORMATS.includes(`.${selectedFile.name?.split('.').pop()}`)
          ) {
            onCreateRun(intentId, 'chat_with_audio', { id: res.fileId, file: selectedFile });
            setIsTranscribing(true);
          }
          setAllFiles([
            ...allFiles,
            { file: selectedFile, id: res.fileId, uploadedAt: Date.now() },
          ]);
        },
        onError: () => {
          toast.error(API_ERROR_MESSAGES.FILE_UPLOAD_FAILED);
          setMessage((prev) => ({
            ...prev,
            files: prev.files.filter((eachFile) => eachFile.file.name !== selectedFile.name),
          }));
          setAllFiles(allFiles.filter((eachFile) => eachFile.id !== ''));
          if (message.files.length === 0) {
            setCurrentFileFormat('');
          }
        },
      }
    );
  };

  const onRemoveFile = (fileToRemove: FileWithId) => {
    if (message.files.length === 1) {
      setCurrentFileFormat('');
    }
    setMessage((prev) => ({
      ...prev,
      files: prev.files.filter((eachFile) => eachFile.file !== fileToRemove.file),
      allFiles: prev.allFiles?.filter((eachFile) => eachFile.file !== fileToRemove.file),
    }));

    if (message.files.length === 1 && isError) {
      history.documentId = [];
    }
    setDocumentId({
      documentId: (history.documentId ?? []).filter((id) => id !== fileToRemove.id),
    });
    setAllFiles(allFiles.filter((eachFile) => eachFile.file !== fileToRemove.file));
    removeFileFromContext(fileToRemove.id);
  };

  const renderSimpleInput = (
    <OutlinedInput
      {...commonProps}
      size="small"
      sx={{
        ...commonProps.sx,
        boxShadow: theme.customShadows.card,
        backgroundColor: 'background.paper',
        '& .MuiOutlinedInput-notchedOutline': {
          border: theme.palette.mode === 'dark' ? 'none' : '',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: theme.palette.mode === 'dark' ? 'none' : '',
        },
      }}
      value={message.value}
      onChange={handleChange((value) => {
        setMessage((prev) => ({ ...prev, value }));
      })}
      onBlur={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown(onSubmit)}
      startAdornment={
        <Stack direction="row" gap={1} color="text.secondary" position="relative" mr={1}>
          <Stack display="flex" direction="row" alignItems="center" gap={1}>
            <ChatUpload
              onUpload={handleUpload}
              currentFileFormat={currentFileFormat}
              disabled={Boolean(modelSettings.knowledgeBase.length)}
              allowedFileTypes={allowedFileTypes}
              isUploading={isUploading}
            />
          </Stack>
        </Stack>
      }
      endAdornment={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* <Tooltip title="Enhance Prompt" placement="top" arrow>
            <IconButton disabled={disabled}>
              <Iconify
                icon="heroicons:sparkles"
                color={!disabled ? theme.palette.primary.main : theme.palette.grey[400]}
              />
            </IconButton>
          </Tooltip> */}
          <SendButton disabled={shouldDisabled} onClick={onSubmit} />
        </Box>
      }
    />
  );

  const renderInputWithFile = (
    <Card
      sx={{
        border: theme.palette.mode === 'light' ? `2px solid ${theme.palette.text.primary}` : 'none',
      }}
    >
      <InputBase
        {...commonProps}
        sx={{
          ...commonProps.sx,
          p: 2,
          flex: 1,
        }}
        placeholder="Instruct Halo what to do with the file (e.g., 'summarize in 100 words')"
        value={message.value}
        onKeyDown={handleKeyDown(onSubmit)}
        onChange={handleChange((value) => {
          setMessage((prev) => ({ ...prev, value }));
        })}
      />
      <Stack direction="row" flexWrap="wrap" gap={1} paddingX={2}>
        {allFiles.map(
          (eachFile, index) =>
            (!!eachFile.id || (isUploadPending && index === allFiles.length - 1)) && (
              <Chip
                key={`${eachFile.file.name}-${index}`}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isUploadPending && index === allFiles.length - 1 ? (
                      <>
                        <CircularProgress size={20} />
                        <FileName
                          name={eachFile.file.name}
                          sx={{
                            maxWidth: '150px',
                          }}
                        />
                      </>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        data-testid="file-thumbnail"
                      >
                        {!!eachFile.id && (
                          <>
                            <FileThumbnail
                              file={eachFile.file.name}
                              sx={{ width: 20, height: 20 }}
                            />
                            {eachFile.file.name && (
                              <span>
                                <FileName
                                  name={eachFile.file.name}
                                  sx={{
                                    maxWidth: '150px',
                                  }}
                                />
                              </span>
                            )}
                          </>
                        )}
                        {!!eachFile.id && (
                          <Link
                            color="inherit"
                            sx={{
                              cursor: history.isLoading ? 'not-allowed' : 'pointer',
                              display: 'inherit',
                            }}
                            onClick={() => !history.isLoading && onRemoveFile(eachFile)}
                          >
                            <Iconify
                              icon="material-symbols:close"
                              sx={{ width: 20, height: 20 }}
                              data-testid="file-close-icon"
                            />
                          </Link>
                        )}
                      </Stack>
                    )}
                  </Stack>
                }
                variant="outlined"
                sx={{
                  border: 'unset',
                  background: theme.palette.background.neutral,
                  color: theme.palette.text.disabled,
                  mb: 1,
                }}
              />
            )
        )}
      </Stack>
      <Divider orientation="horizontal" sx={{ py: 0.5 }} />
      <Stack
        display="flex"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1}
      >
        <FlexStack spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {canUploadMoreFiles && !AUDIO_FILE_FORMATS.includes(currentFileFormat ?? '') && (
            <ChatUpload
              onUpload={handleUpload}
              currentFileFormat={currentFileFormat}
              disabled={Boolean(modelSettings.knowledgeBase.length)}
              allowedFileTypes={allowedFileTypes}
              isUploading={isUploading}
            />
          )}
        </FlexStack>
        <Box>
          {/* <Tooltip title="Enhance Prompt" placement="top" arrow>
            <Typography component="span">
              <IconButton disabled={disabled}>
                <Iconify
                  icon="heroicons:sparkles"
                  color={!disabled ? theme.palette.primary.main : theme.palette.text.disabled}
                />
              </IconButton>
            </Typography>
          </Tooltip> */}
          <SendButton disabled={shouldDisabled} onClick={onSubmit} />
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        maxWidth: `${W_MAX + PX_INPUT * 2}px`,
        padding: {
          md: isEmpty ? emptyPadding : `26px 0px 22px 0px`,
          xs: isEmpty ? emptyPadding : `${PX_INPUT}px 0px 0px 0px`,
        },
        borderRadius: `${theme.shape.borderRadius}px`,
        background:
          theme.palette.mode == 'dark'
            ? theme.palette.background.neutral
            : 'linear-gradient(160deg, rgba(208, 239, 255, 0.2) -16.37%, rgba(198, 204,255, 0.2) 107.88%)',
        ...(!isEmpty && {
          background: 'unset',
          maxWidth: `${W_MAX}px`,
        }),
      }}
    >
      {allFiles.length === 0 ? renderSimpleInput : renderInputWithFile}
      {isEmpty && <ChatTips />}
      <ChatDisclaimer />
    </Box>
  );
}
export default ChatInputBox;
